import { Button } from '@positivote/design-system/components/Button'
import { Div } from '@positivote/design-system/components/Div'
import { Divider } from '@positivote/design-system/components/Divider'
import { Grid } from '@positivote/design-system/components/Grid'
import { IconButton } from '@positivote/design-system/components/IconButton'
import { Image } from '@positivote/design-system/components/Image'
import { Typography } from '@positivote/design-system/components/Typography'
import { useAlert, useTheme } from '@positivote/design-system/hooks'
import { CopyAllIcon } from '@positivote/design-system/icons/CopyAll'
import { LoginIcon } from '@positivote/design-system/icons/Login'
import { Breakpoint } from '@positivote/design-system/theme'
import { useState } from 'react'
import { browserName, browserVersion, osName, osVersion } from 'react-device-detect'
import { useParams } from 'react-router-dom'

import { i18n } from '@/common/i18n'
import { BaseFooter } from '@/common/layouts/BaseFooter'
import { AccordionAudio } from '@/modules/requirements/components/AccordionAudio'
import { AccordionBrowser } from '@/modules/requirements/components/AccordionBrowser'
import { AccordionHardware } from '@/modules/requirements/components/AccordionHardware'
import { AccordionNetworkConnection } from '@/modules/requirements/components/AccordionNetworkConnection'
import { AccordionOperationalSystem } from '@/modules/requirements/components/AccordionOperationalSystem'
import { AccordionServices } from '@/modules/requirements/components/AccordionServices'
import { AccordionVideo } from '@/modules/requirements/components/AccordionVideo'
import { HelpForm } from '@/modules/requirements/components/HelpForm'
import { useShowSystemRequirement } from '@/modules/requirements/hooks'

export function SystemRequirementsDetails(): JSX.Element {
  const { breakpoint } = useTheme()
  const systemRequirement = useShowSystemRequirement({})
  const params = useParams()
  const { addAlertMessage } = useAlert()
  const [audioResult, setAudioResult] = useState('')
  const [videoResult, setVideoResult] = useState('')
  const [networkInfos, setNetworkInfos] = useState<{
    speed: number | undefined
    ip: string | undefined
  }>()
  const [hardwareInfos, setHardwareInfos] = useState<{
    screenSize: string
    isTouchScreen: string
    deviceType: string
  }>()
  const [servicesInfos, setServicesInfos] = useState<{ service: string; database: string }>()

  const productName = params.name

  const currentProductSystemRequirement =
    systemRequirement.data?.product.find((product) => product.name === productName) ??
    systemRequirement.data?.product.find((product) => product.name === 'hub')

  const currentBrowser = currentProductSystemRequirement?.requirements.browsers.find(
    (name) => name.name === browserName.toLowerCase()
  )

  const currentSystem = currentProductSystemRequirement?.requirements.operatingSystems.find(
    (name) => name.name === osName.toLowerCase()
  )

  const copyResults = `--
${i18n().modules.requirements.pages.details.resultPayload.browser}
${i18n().modules.requirements.pages.details.resultPayload.type}: ${browserName}
${i18n().modules.requirements.pages.details.resultPayload.version}: ${browserVersion}
--
${i18n().modules.requirements.pages.details.resultPayload.operationalSystem}
${i18n().modules.requirements.pages.details.resultPayload.type}: ${osName}
${i18n().modules.requirements.pages.details.resultPayload.version}: ${osVersion}
--
${i18n().modules.requirements.pages.details.resultPayload.hardware}
${i18n().modules.requirements.pages.details.resultPayload.screenSize}: ${hardwareInfos?.screenSize}
${i18n().modules.requirements.pages.details.resultPayload.touch}: ${hardwareInfos?.isTouchScreen}
${i18n().modules.requirements.pages.details.resultPayload.deviceType}: ${hardwareInfos?.deviceType}
--
${i18n().modules.requirements.pages.details.resultPayload.networkConnection}
${i18n().modules.requirements.pages.details.resultPayload.ipAddress}: ${networkInfos?.ip}
${i18n().modules.requirements.pages.details.resultPayload.internetSpeed}: ${networkInfos?.speed} Mbps
--
${i18n().modules.requirements.pages.details.resultPayload.audio}: ${!audioResult.length ? i18n().modules.requirements.pages.details.resultPayload.noTest : audioResult === 'pass' ? i18n().modules.requirements.pages.details.resultPayload.success : i18n().modules.requirements.pages.details.resultPayload.failure}
--
${i18n().modules.requirements.pages.details.resultPayload.video}: ${!videoResult.length ? i18n().modules.requirements.pages.details.resultPayload.noTest : videoResult === 'pass' ? i18n().modules.requirements.pages.details.resultPayload.success : i18n().modules.requirements.pages.details.resultPayload.failure}
--
${i18n().modules.requirements.pages.details.resultPayload.service}: ${servicesInfos?.service}
${i18n().modules.requirements.pages.details.resultPayload.dataBase}: ${servicesInfos?.database}`

  function handleCopyInfos(): void {
    void navigator.clipboard.writeText(copyResults)

    addAlertMessage({
      alertKey: 'copy',
      subTitle: i18n().modules.requirements.pages.details.alert.subTitle,
      severity: 'success'
    })
  }
  return (
    <Div
      css={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'auto'
      }}
    >
      <Div
        css={{
          display: 'grid',
          justifyContent: 'center',
          width: '100%',
          padding: '$lg $none',
          position: 'relative'
        }}
      >
        <Image
          alt="logo"
          src={currentProductSystemRequirement?.logo}
          css={{
            width: 178,
            height: 48,
            '@xs': { width: 148, height: 40 },
            objectFit: 'cover'
          }}
        />
        {breakpoint === Breakpoint.xs ? (
          <IconButton
            onClick={() =>
              window.location.assign(
                currentProductSystemRequirement?.urlLogin ?? import.meta.env.VITE_HUB_URL_LOGIN
              )
            }
            css={{ position: 'absolute', right: 20 }}
          >
            <LoginIcon />
          </IconButton>
        ) : (
          <Button
            onClick={() =>
              window.location.assign(
                currentProductSystemRequirement?.urlLogin ?? import.meta.env.VITE_HUB_URL_LOGIN
              )
            }
            css={{ position: 'absolute', right: 62 }}
            LeadingIcon={<LoginIcon />}
          >
            {i18n().modules.requirements.pages.details.login}
          </Button>
        )}
      </Div>
      <Divider />
      <Div
        css={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          alignItems: 'center',
          padding: '$lg',
          '@xs': { padding: '$md' }
        }}
      >
        <Typography
          variant="headlineSmall"
          css={{ textAlign: 'center', color: '$on-surface', margin: '$md $none' }}
        >
          {i18n().modules.requirements.pages.details.pageTitle}
        </Typography>
        <Grid
          xl={6}
          md={10}
          sm={12}
          xs={12}
          css={{
            flexDirection: 'column',
            gridGap: '$md',
            display: 'grid'
          }}
        >
          {!(browserName === 'none' && browserVersion === 'none') && (
            <AccordionBrowser browser={currentBrowser} isLoading={systemRequirement.isLoading} />
          )}
          <AccordionOperationalSystem
            operatingSystems={currentSystem}
            isLoading={systemRequirement.isLoading}
          />
          <AccordionHardware
            isLoading={systemRequirement.isLoading}
            onChangeHardwareInfos={(infos) => setHardwareInfos(infos)}
          />
          <AccordionNetworkConnection onChangeNetworkInfos={(speed) => setNetworkInfos(speed)} />
          <AccordionAudio
            urlAudio={systemRequirement.data?.urlAudio}
            onChangeAudioResult={(result) => setAudioResult(result)}
          />
          <AccordionVideo
            urlVideo={systemRequirement.data?.urlVideo}
            onChangeVideoResult={(result) => setVideoResult(result)}
          />
          <AccordionServices onChangeServicesInfos={(infos) => setServicesInfos(infos)} />
          <HelpForm result={copyResults} product={productName} />
          <Div
            css={{
              background: '$surface-2',
              borderRadius: '$lg',
              padding: '$lg',
              margin: '$md',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="bodyMedium"
              css={{ color: '$on-surface-variant', textAlign: 'center' }}
            >
              {i18n().modules.requirements.pages.details.helpSubTitle}
            </Typography>
            <Button LeadingIcon={<CopyAllIcon />} variant="outlined" onClick={handleCopyInfos}>
              {i18n().modules.requirements.pages.details.buttons.copyResult}
            </Button>
          </Div>
          <BaseFooter />
        </Grid>
      </Div>
    </Div>
  )
}
